import type { UseFormSetError, FieldValues, Path } from 'react-hook-form';
import { isResourceRequestError } from '../client.js';

export interface ServerErrorResult {
  /** Errors that matched a form field and were placed on it. */
  handled: string[];
  /** Message for the form-level alert: what was left over, or the whole error. */
  message: string | null;
}

/**
 * Maps a 422's `fields` map back onto the form.
 *
 * The one rule that matters: an error whose key is not a field must still be
 * shown. Silently dropping it is how a form ends up refusing to submit with no
 * visible reason — the failure mode this function exists to prevent.
 */
export function applyServerErrors<TValues extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<TValues>,
  fieldNames: readonly string[],
): ServerErrorResult {
  if (!isResourceRequestError(error)) {
    return { handled: [], message: error instanceof Error ? error.message : 'Errore imprevisto.' };
  }

  const known = new Set(fieldNames);
  const handled: string[] = [];
  const orphans: string[] = [];

  for (const [field, messages] of Object.entries(error.error.fields ?? {})) {
    const message = messages.join(' ');
    if (known.has(field)) {
      setError(field as Path<TValues>, { type: 'server', message });
      handled.push(field);
    } else {
      orphans.push(`${field}: ${message}`);
    }
  }

  if (orphans.length > 0) return { handled, message: orphans.join('\n') };
  if (handled.length > 0) return { handled, message: null };
  return { handled, message: error.error.message };
}

export function fieldNamesOf(
  sections: readonly { fields: readonly { name: string }[] }[],
): string[] {
  return sections.flatMap((section) => section.fields.map((field) => field.name));
}
